import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';
import IdentityInbox from '../components/IdentityInbox';
import NewAccountModal from '../components/NewAccountModal';
import { useSente, SenteIdentity, SenteRegistration, SenteApiError } from '../hooks/useSente';
import { RegistrationStatusBadge, RunStatusBadge, AccountKindBadge } from '../components/AccountStatusBadges';

const LIVE_RUN_STATUSES = ['queued', 'running', 'awaiting_verification', 'blocked'];

const appHost = (appUrl: string) => {
  try {
    return new URL(appUrl).host;
  } catch {
    return appUrl;
  }
};

const IdentityDetail: React.FC = () => {
  const { identityId } = useParams<{ identityId: string }>();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { ready, getIdentity, listRegistrations, sendMessage, createRegistration, loginRegistration, revokeConnection } = useSente();

  const [identity, setIdentity] = useState<SenteIdentity | null>(null);
  const [registrations, setRegistrations] = useState<SenteRegistration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [inboxRefresh, setInboxRefresh] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [acctError, setAcctError] = useState<React.ReactNode>(null);

  // Compose modal
  const [showCompose, setShowCompose] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<React.ReactNode>(null);

  // New Account modal (shared component; this identity is the parent — no identity picker).
  const [createModal, setCreateModal] = useState<{ open: boolean; mode: 'register' | 'connect'; prefillUrl: string }>({
    open: false,
    mode: 'register',
    prefillUrl: '',
  });

  useEffect(() => {
    if (!ready || !identityId) return;
    let cancelled = false;
    setIsLoading(true);
    getIdentity(identityId)
      .then(idn => { if (!cancelled) setIdentity(idn); })
      .catch((e: any) => { if (!cancelled) setError(e.message || 'Failed to load identity'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    // Connected accounts (registrations) — context only; a failure here shouldn't error the page.
    listRegistrations(identityId)
      .then(regs => { if (!cancelled) setRegistrations(regs); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ready, identityId, getIdentity, listRegistrations]);

  const copy = (e: React.MouseEvent, key: string, value: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(prev => (prev === key ? null : prev)), 1500);
  };

  const handleSend = async () => {
    if (!identityId) return;
    setIsSending(true);
    setSendError(null);
    try {
      await sendMessage({ identityId, to: composeTo.trim(), subject: composeSubject, text: composeBody });
      setShowCompose(false);
      setComposeTo('');
      setComposeSubject('');
      setComposeBody('');
      setInboxRefresh(n => n + 1);
    } catch (e: any) {
      if (e instanceof SenteApiError && e.code === 'SEND_LIMIT_EXCEEDED') {
        setSendError(
          <>
            Daily send limit reached.{' '}
            <Link to="/billing" className="underline font-medium">Upgrade</Link>
          </>
        );
      } else {
        setSendError(e.message || 'Failed to send message');
      }
    } finally {
      setIsSending(false);
    }
  };

  const runLimitError = (e: any): React.ReactNode =>
    e instanceof SenteApiError && e.code === 'RUN_LIMIT_EXCEEDED'
      ? 'Daily run limit reached for your org — try again tomorrow.'
      : e.message || 'Something went wrong';

  // Row action. Active → log in (works for created + connected). A non-active CONNECTED row can't be
  // retried via createRegistration (409 ALREADY_CONNECTED) — reopen the connect modal to re-supply
  // credentials. A non-active created row retries the register.
  const startRun = async (e: React.MouseEvent, reg: SenteRegistration) => {
    e.stopPropagation();
    if (reg.status !== 'active' && reg.kind === 'connected') {
      openConnect(reg.appUrl);
      return;
    }
    setBusyId(reg.id);
    setAcctError(null);
    try {
      const { run } =
        reg.status === 'active'
          ? await loginRegistration(reg.id)
          : await createRegistration({ identityId: reg.identityId, appUrl: reg.appUrl });
      navigate(`/runs/${run.id}`);
    } catch (err: any) {
      setAcctError(runLimitError(err));
      setBusyId(null);
    }
  };

  // Revoke a connection (withdraw the delegation; the vault is kept so a reconnect re-enables it).
  const revoke = async (e: React.MouseEvent, reg: SenteRegistration) => {
    e.stopPropagation();
    setBusyId(reg.id);
    setAcctError(null);
    try {
      await revokeConnection(reg.id);
      if (identityId) setRegistrations(await listRegistrations(identityId));
    } catch (err: any) {
      setAcctError(err?.message || 'Failed to revoke the connection');
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = () => setCreateModal({ open: true, mode: 'register', prefillUrl: '' });
  const openConnect = (prefillUrl = '') => setCreateModal({ open: true, mode: 'connect', prefillUrl });

  return (
    <div className="h-full overflow-hidden flex flex-col bg-white dark:bg-slate-950">
      <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="flex flex-1 min-h-0 relative">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumbs
              items={[{ label: 'Identities', path: '/' }, { label: identity?.email || '…' }]}
              className="mb-6"
            />

            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : identity && (
              <>
                {/* Identity header card */}
                <div className="p-6 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 mb-8">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
                        {identity.name || identity.email.split('@')[0]}
                      </h1>
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400 mb-2">
                        <span className="font-mono truncate">{identity.email}</span>
                        <button
                          onClick={e => copy(e, 'email', identity.email)}
                          className="p-1 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors flex-shrink-0"
                          title="Copy email"
                        >
                          {copiedKey === 'email' ? (
                            <span className="text-xs text-green-600 dark:text-green-400 px-1">Copied</span>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      {/* Phone is a reserved identity channel (SMS verification) — not yet available. */}
                      <div
                        className="flex items-center gap-2 text-sm mb-2 opacity-60 select-none"
                        title="Phone numbers (SMS verification) are coming soon"
                      >
                        <svg className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-gray-500 dark:text-slate-400">No phone number</span>
                        <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400">
                          Coming soon
                        </span>
                      </div>
                      {identity.description && (
                        <p className="text-sm text-gray-600 dark:text-slate-400 mb-2">{identity.description}</p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-slate-500">
                        Created {new Date(identity.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => { setSendError(null); setShowCompose(true); }}
                      className="px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 dark:from-blue-600 dark:to-purple-600 dark:hover:from-blue-500 dark:hover:to-purple-500 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-gray-500/25 dark:shadow-blue-500/25 flex items-center space-x-2 flex-shrink-0 ml-4"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Compose</span>
                    </button>
                  </div>
                </div>

                {/* Accounts (registrations for this identity) */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Accounts
                      {registrations.length > 0 && (
                        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-slate-400">
                          {registrations.length}
                        </span>
                      )}
                    </h2>
                    <button
                      data-tour="new-account"
                      onClick={openCreate}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <path d="M12 4v16m8-8H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      New Account
                    </button>
                  </div>
                  {acctError && (
                    <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                      {acctError}
                    </div>
                  )}
                  {registrations.length === 0 ? (
                    <div className="p-4 bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-dashed border-gray-200 dark:border-slate-800 text-sm text-gray-500 dark:text-slate-400">
                      This identity isn't registered with any app yet.{' '}
                      <button onClick={openCreate} className="underline font-medium">Register it with an app</button>
                      {' '}and the managed browser signs up, verifies the email, and keeps it signed in.
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-slate-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
                      {registrations.map(reg => {
                        const run = reg.latestRun;
                        const liveRun = run && LIVE_RUN_STATUSES.includes(run.status);
                        return (
                          <div
                            key={reg.id}
                            onClick={() => run && navigate(`/runs/${run.id}`)}
                            className={`flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors ${run ? 'cursor-pointer' : ''}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-900 dark:text-white truncate">
                                  {appHost(reg.appUrl)}
                                </span>
                                <AccountKindBadge kind={reg.kind} />
                                <RegistrationStatusBadge status={reg.status} />
                              </div>
                              {reg.username && (
                                <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                                  user: <span className="font-mono">{reg.username}</span>
                                </div>
                              )}
                            </div>
                            {run && <RunStatusBadge status={run.status} />}
                            {/* Revoke a live (non-revoked) connection. */}
                            {reg.kind === 'connected' && reg.status !== 'disabled' && (
                              <button
                                onClick={e => revoke(e, reg)}
                                disabled={busyId === reg.id}
                                className="px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg font-medium transition-colors text-sm disabled:opacity-50 flex-shrink-0"
                              >
                                Revoke
                              </button>
                            )}
                            {liveRun ? (
                              <button
                                onClick={e => { e.stopPropagation(); navigate(`/runs/${run!.id}`); }}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm flex-shrink-0"
                              >
                                Watch
                              </button>
                            ) : (
                              <button
                                onClick={e => startRun(e, reg)}
                                disabled={busyId === reg.id}
                                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-900 dark:text-white rounded-lg font-medium transition-colors text-sm disabled:opacity-50 flex-shrink-0"
                              >
                                {busyId === reg.id
                                  ? 'Starting…'
                                  : reg.status === 'active'
                                    ? 'Log in'
                                    : reg.kind === 'connected'
                                      ? 'Reconnect'
                                      : 'Retry'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Inbox */}
                <IdentityInbox identityId={identityId!} email={identity.email} refreshSignal={inboxRefresh} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Compose modal */}
      {showCompose && identity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !isSending && setShowCompose(false)}>
          <div
            className="w-full max-w-lg p-6 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Compose</h2>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-mono mb-4">From: {identity.email}</p>

            {sendError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                {sendError}
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">To</label>
            <input
              type="email"
              value={composeTo}
              onChange={e => setComposeTo(e.target.value)}
              placeholder="someone@example.com"
              className="w-full mb-4 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Subject</label>
            <input
              type="text"
              value={composeSubject}
              onChange={e => setComposeSubject(e.target.value)}
              className="w-full mb-4 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Body</label>
            <textarea
              value={composeBody}
              onChange={e => setComposeBody(e.target.value)}
              rows={6}
              className="w-full mb-6 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCompose(false)}
                disabled={isSending}
                className="px-4 py-2 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={isSending || !composeTo.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
              >
                {isSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Account modal — registers/connects under THIS identity */}
      {identity && (
        <NewAccountModal
          open={createModal.open}
          onClose={() => setCreateModal(m => ({ ...m, open: false }))}
          identity={identity}
          initialMode={createModal.mode}
          prefillUrl={createModal.prefillUrl}
        />
      )}
    </div>
  );
};

export default IdentityDetail;
